import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Squares2X2Icon,
  ChartBarIcon,
  BriefcaseIcon,
  ClockIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BeakerIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

import UserIdEntry from './components/UserIdEntry';
import AppHeader from './components/AppHeader';
import FileUpload from './components/FileUpload';
import ControlPanel from './components/ControlPanel';
import FileList from './components/FileList';
import JobTracker from './components/JobTracker';
import TopProgressBar from './components/TopProgressBar';
import MetricsDashboard from './components/MetricsDashboard';
import SingleRequirementAnalysis from './components/SingleRequirementAnalysis';
import { api } from './services/api';
import {
  clearTokens,
  getAccessToken,
  getIdentity,
  setTokens,
  subscribeToAuthChanges,
} from './services/authTokens';
import UserObservability from './components/UserObservability';
import ReportsTab from './components/ReportsTab';

// API_BASE_URL is controlled via .env file (REACT_APP_SSD_FRONT__API_URL):
// - Local development: set to 'http://localhost:8001' (direct backend connection)
// - Docker/production: leave empty or unset to use '/api' (nginx proxy)
const API_BASE_URL = process.env.REACT_APP_SSD_FRONT__API_URL || '/api';

function App() {
  const initialIdentity = getIdentity() || {};
  const [userId, setUserId] = useState(initialIdentity.user_id || '');
  const [userEmail, setUserEmail] = useState(initialIdentity.email || '');
  const [isUserIdSet, setIsUserIdSet] = useState(!!getAccessToken() && !!initialIdentity.user_id);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [toastMessage, setToastMessage] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [highlightedReport, setHighlightedReport] = useState(null);
  const [showInstructions, setShowInstructions] = useState(null);
  const [fileType, setFileType] = useState('requirements');

  const [expandedCategories, setExpandedCategories] = useState({
    Requirements: true,
    Reports: true,
    Rules: true
  });

  const [selectedRequirementFile, setSelectedRequirementFile] = useState(null);
  const [selectedRulesFile, setSelectedRulesFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Queue System States
  const [activeJobs, setActiveJobs] = useState([]);
  const [jobStatuses, setJobStatuses] = useState({}); // WebSocket status data keyed by job ID
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0);
  const dismissedJobIdsRef = useRef(new Set()); // Track dismissed jobs to prevent reappearing bars

  // Sidebar state
  const [activeTab, setActiveTab] = useState('batch-processing');
  const [showControlPanel, setShowControlPanel] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load sidebar state from localStorage
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState === 'true';
  });

  // Rules and Architecture state
  const [manualRulesByDocType, setManualRulesByDocType] = useState({});
  const [globalRulesByDocType, setGlobalRulesByDocType] = useState({});
  const [selectedManualRules, setSelectedManualRules] = useState([]);
  const [selectedGlobalRules, setSelectedGlobalRules] = useState([]);
  const [availableArchitectures, setAvailableArchitectures] = useState([]);
  const [selectedArchitecture, setSelectedArchitecture] = useState('');
  const [isLoadingRules, setIsLoadingRules] = useState(false);

  // SFD requirement scope state (table-variant SFD docs only — see ControlPanel).
  // '' = default (narrative "Functional Requirements" heading match);
  // 'frm_heading' = requirements under an "FRM" heading;
  // 'artifact_type_requirement' = Artifact Type field contains "Requirement".
  const [sfdScope, setSfdScope] = useState('feature_arch');

  // DOCX filter state
  const [docxFunctions, setDocxFunctions] = useState([]);
  const [selectedFunctions, setSelectedFunctions] = useState([]);
  const [docxStates, setDocxStates] = useState([]);
  const [selectedStates, setSelectedStates] = useState([]);
  const [docxMaturities, setDocxMaturities] = useState([]);
  const [selectedMaturities, setSelectedMaturities] = useState([]);
  const [docxRequirements, setDocxRequirements] = useState([]);
  const [isLoadingDocxFilters, setIsLoadingDocxFilters] = useState(false);

  // Resizable control panel
  const PANEL_MIN_WIDTH = 384; // 24rem
  const PANEL_MAX_WIDTH = 900;
  const [controlPanelWidth, setControlPanelWidth] = useState(() => {
    const saved = localStorage.getItem('controlPanelWidth');
    return saved ? Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, Number(saved))) : PANEL_MIN_WIDTH;
  });
  const panelDragRef = useRef(null);

  useLayoutEffect(() => {
    const ref = panelDragRef.current;
    if (!ref) return;

    let startX = 0;
    let startWidth = 0;

    const onMove = (e) => {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const newWidth = Math.max(PANEL_MIN_WIDTH, Math.min(PANEL_MAX_WIDTH, startWidth + (startX - clientX)));
      setControlPanelWidth(newWidth);
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setControlPanelWidth((w) => {
        localStorage.setItem('controlPanelWidth', String(w));
        return w;
      });
    };

    const onDown = (e) => {
      e.preventDefault();
      startX = e.touches ? e.touches[0].clientX : e.clientX;
      startWidth = controlPanelWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend', onUp);
    };

    ref.addEventListener('mousedown', onDown);
    ref.addEventListener('touchstart', onDown);
    return () => {
      ref.removeEventListener('mousedown', onDown);
      ref.removeEventListener('touchstart', onDown);
    };
  }); // intentionally no deps — needs current controlPanelWidth in closure

  // SSO gateway auth state
  const [authConfig, setAuthConfig] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const authInitStartedRef = useRef(false);

  useEffect(() => {
    if (authInitStartedRef.current) {
      return;
    }
    authInitStartedRef.current = true;

    const initAuth = async () => {
      try {
        const response = await api.getAuthConfig();
        setAuthConfig(response.data);

        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('auth_code');

        if (authCode) {
          try {
            const tokenResponse = await api.exchangeAuthCode(authCode);
            const data = tokenResponse.data || {};
            setTokens({
              accessToken: data.access_token,
              expiresIn: data.expires_in,
              identity: { user_id: data.user_id || '', email: data.email || '' },
            });
            window.location.replace(response.data?.callback_path || '/');
            return;
          } catch {
            setError('Sign-in failed. Please try again.');
            setAuthLoading(false);
            return;
          }
        }

        // No auth_code in URL — hydrate from any stored session token
        if (getAccessToken()) {
          const identity = getIdentity() || {};
          if (identity.user_id) {
            setUserId(identity.user_id);
            setUserEmail(identity.email || '');
            setIsUserIdSet(true);
          }
        }
      } catch (err) {
        console.error('Error fetching auth config:', err);
        setAuthConfig((prev) => prev ?? { sso_enabled: false });
      } finally {
        setAuthLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    return subscribeToAuthChanges((current) => {
      if (current && current.identity && current.identity.user_id) {
        setUserId(current.identity.user_id);
        setUserEmail(current.identity.email || '');
        setIsUserIdSet(true);
      } else {
        setIsUserIdSet(false);
        setUserId('');
        setUserEmail('');
      }
    });
  }, []);

  useEffect(() => {
    if (isUserIdSet && userId.trim()) {
      loadFiles();
      loadRulesAndArchitectures();
    }
  }, [isUserIdSet, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRulesAndArchitectures = async () => {
    setIsLoadingRules(true);
    try {
      const [rulesResponse, architecturesResponse] = await Promise.all([
        api.getAllRules(),
        api.getAvailableArchitectures()
      ]);

      setManualRulesByDocType(rulesResponse.data.manual_rules || {});
      setGlobalRulesByDocType(rulesResponse.data.global_rules || {});
      setAvailableArchitectures(architecturesResponse.data.architectures || []);
    } catch (err) {
      console.error('Error loading rules and architectures:', err);
    } finally {
      setIsLoadingRules(false);
    }
  };

  const resetRuleSelection = useCallback(() => {
    const docType = selectedRequirementFile?.file_standard;
    if (docType) {
      const manualRules = manualRulesByDocType[docType] || [];
      const globalRules = globalRulesByDocType[docType] || [];
      setSelectedManualRules(manualRules.map(r => r.id));
      setSelectedGlobalRules(globalRules.map(r => r.id));
    }
  }, [selectedRequirementFile, manualRulesByDocType, globalRulesByDocType]);

  useEffect(() => {
    if (selectedRequirementFile) {
      resetRuleSelection();
    }
  }, [selectedRequirementFile?.file_standard, resetRuleSelection]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load DOCX filters when a DOCX file is selected
  useEffect(() => {
    if (selectedRequirementFile?.file_standard === 'DOCX') {
      const loadDocxFilters = async () => {
        setIsLoadingDocxFilters(true);
        try {
          const response = await api.getDocxFilters(selectedRequirementFile.name);
          const data = response.data || {};
          const funcs = data.functions || [];
          const states = data.states || [];
          const maturities = data.maturities || [];
          setDocxFunctions(funcs);
          setSelectedFunctions(funcs.map(f => f.name));
          setDocxStates(states);
          setSelectedStates(states.map(s => s.name));
          setDocxMaturities(maturities);
          setSelectedMaturities(maturities.map(m => m.name));
          setDocxRequirements(data.requirements || []);
        } catch (err) {
          console.error('Error loading DOCX filters:', err);
          setDocxFunctions([]);
          setSelectedFunctions([]);
          setDocxStates([]);
          setSelectedStates([]);
          setDocxMaturities([]);
          setSelectedMaturities([]);
          setDocxRequirements([]);
        } finally {
          setIsLoadingDocxFilters(false);
        }
      };
      loadDocxFilters();
    } else {
      setDocxFunctions([]);
      setSelectedFunctions([]);
      setDocxStates([]);
      setSelectedStates([]);
      setDocxMaturities([]);
      setSelectedMaturities([]);
      setDocxRequirements([]);
    }
  }, [selectedRequirementFile?.name, selectedRequirementFile?.file_standard]);

  const handleUserIdSubmit = async (e) => {
    e.preventDefault();
    if (!userId.trim()) {
      setError('Please enter a User ID');
      return;
    }
    try {
      const response = await api.devLogin(userId.trim(), userEmail || undefined);
      const data = response.data || {};
      setTokens({
        accessToken: data.access_token,
        expiresIn: data.expires_in,
        identity: { user_id: data.user_id || userId.trim().toUpperCase(), email: data.email || userEmail || '' },
      });
      setUserId(data.user_id || userId.trim().toUpperCase());
      setUserEmail(data.email || userEmail || '');
      setError('');
      setIsUserIdSet(true);
    } catch (err) {
      const msg = err?.userMessage || err?.response?.data?.detail || 'Dev login failed';
      setError(typeof msg === 'string' ? msg : 'Dev login failed');
    }
  };

  const handleLogout = async () => {
    const callbackPath = authConfig?.callback_path || '/';
    const returnUrl = `${window.location.origin}${callbackPath}`;
    let redirect = returnUrl;
    try {
      const response = await api.logout(returnUrl);
      redirect = response?.data?.redirect || returnUrl;
    } catch {
      /* fall through to local cleanup */
    }
    clearTokens();
    setIsUserIdSet(false);
    setUserId('');
    setUserEmail('');
    setFileList([]);
    setSelectedFile(null);
    setUploadResult(null);
    setError('');
    setSelectedRequirementFile(null);
    setSelectedRulesFile(null);
    setActiveJobs([]);
    setJobStatuses({});
    window.location.replace(redirect);
  };

  const loadFiles = async () => {
    setIsLoadingFiles(true);
    try {
      const response = await api.listFiles(userId);
      // The API returns an object with reports, requirements, and rules arrays
      const data = response.data || {};
      if (Array.isArray(data.requirements)) {
        data.requirements.sort((a, b) => a.name.localeCompare(b.name));
      }
      setFileList(data);
    } catch (err) {
      console.error('Error loading files:', err);
      setError('Failed to load files');
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];

    if (file) {
      let isValid = false;

      // Validate file based on type
      if (fileType === 'requirements') {
        // Requirements can be Excel or Word documents
        isValid = file.name.match(/\.(xlsx?|xls|docx?)$/i);

        if (!isValid) {
          setError(
            'Requirements files must be Excel (.xlsx, .xls) or Word (.docx, .doc) format'
          );
          setSelectedFile(null);
          return;
        }
      } else if (fileType === 'rules') {
        // Rules must be Excel only
        isValid = file.name.match(/\.(xlsx?|xls)$/i);

        if (!isValid) {
          setError('Rules files must be Excel format (.xlsx or .xls)');
          setSelectedFile(null);
          return;
        }
      }

      setSelectedFile(file);
      setError('');
      setUploadResult(null);
    } else {
      setSelectedFile(null);
      setError('');
      setUploadResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;

    if (files && files[0]) {
      const file = files[0];
      let isValid = false;

      if (fileType === 'requirements') {
        isValid = file.name.match(/\.(xlsx?|xls|docx?)$/i);

        if (!isValid) {
          setError(
            'Requirements files must be Excel (.xlsx, .xls) or Word (.docx, .doc) format'
          );
          setSelectedFile(null);
          return;
        }
      } else if (fileType === 'rules') {
        isValid = file.name.match(/\.(xlsx?|xls)$/i);

        if (!isValid) {
          setError('Rules files must be Excel format (.xlsx or .xls)');
          setSelectedFile(null);
          return;
        }
      }

      setSelectedFile(file);
      setError('');
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }

    if (!userId) {
      setError('User ID is required for upload');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile, selectedFile.name);
    formData.append('file_type', fileType);

    setIsUploading(true);
    setError('');
    setUploadResult(null);

    try {
      const response = await api.uploadFile(userId, formData);

      setUploadResult({
        success: true,
        message: `File uploaded successfully as ${fileType}!`,
        data: response.data
      });

      setTimeout(() => setUploadResult(null), 5000);

      setSelectedFile(null);
      loadFiles();
    } catch (err) {
      let errorMessage = 'Failed to upload file';

      // Better error message extraction
      if (err.response?.data) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data.detail) {
          if (typeof err.response.data.detail === 'string') {
            errorMessage = err.response.data.detail;
          } else if (Array.isArray(err.response.data.detail)) {
            errorMessage = err.response.data.detail.map(d => d.msg || d.message || d).join(', ');
          } else if (typeof err.response.data.detail === 'object') {
            errorMessage = err.response.data.detail.message || JSON.stringify(err.response.data.detail);
          }
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        } else if (err.response.data.error) {
          errorMessage = err.response.data.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setUploadResult({
        success: false,
        message: errorMessage
      });
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelection = (file, type) => {
    if (type === 'requirements') {
      setSelectedRequirementFile(file.name === selectedRequirementFile?.name ? null : file);
    } else if (type === 'rules') {
      setSelectedRulesFile(file.name === selectedRulesFile?.name ? null : file);
    }
  };

  const handleManualRuleToggle = (ruleId) => {
    setSelectedManualRules(prev =>
      prev.includes(ruleId)
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    );
  };

  const handleGlobalRuleToggle = (ruleId) => {
    setSelectedGlobalRules(prev =>
      prev.includes(ruleId)
        ? prev.filter(id => id !== ruleId)
        : [...prev, ruleId]
    );
  };

  const handleToggleAllManualRules = (selectAll) => {
    const docType = selectedRequirementFile?.file_standard;
    const manualRules = manualRulesByDocType[docType] || [];
    if (selectAll) {
      setSelectedManualRules(manualRules.map(r => r.id));
    } else {
      setSelectedManualRules([]);
    }
  };

  const handleToggleAllGlobalRules = (selectAll) => {
    const docType = selectedRequirementFile?.file_standard;
    const globalRules = globalRulesByDocType[docType] || [];
    if (selectAll) {
      setSelectedGlobalRules(globalRules.map(r => r.id));
    } else {
      setSelectedGlobalRules([]);
    }
  };

  const handleFunctionToggle = (functionName) => {
    setSelectedFunctions(prev =>
      prev.includes(functionName)
        ? prev.filter(n => n !== functionName)
        : [...prev, functionName]
    );
  };

  const handleToggleAllFunctions = (selectAll) => {
    if (selectAll) {
      setSelectedFunctions(docxFunctions.map(f => f.name));
    } else {
      setSelectedFunctions([]);
    }
  };

  const handleStateToggle = (stateName) => {
    setSelectedStates(prev =>
      prev.includes(stateName)
        ? prev.filter(n => n !== stateName)
        : [...prev, stateName]
    );
  };

  const handleToggleAllStates = (selectAll) => {
    if (selectAll) {
      setSelectedStates(docxStates.map(s => s.name));
    } else {
      setSelectedStates([]);
    }
  };

  const handleMaturityToggle = (maturityName) => {
    setSelectedMaturities(prev =>
      prev.includes(maturityName)
        ? prev.filter(n => n !== maturityName)
        : [...prev, maturityName]
    );
  };

  const handleToggleAllMaturities = (selectAll) => {
    if (selectAll) {
      setSelectedMaturities(docxMaturities.map(m => m.name));
    } else {
      setSelectedMaturities([]);
    }
  };

  const filteredRequirementCount = useMemo(() => {
    if (!docxRequirements.length) return null;

    // "All selected" means no filter is applied (matches everything incl. empty values).
    // "None selected" on a non-empty option list means nothing matches.
    const fnAll = selectedFunctions.length === docxFunctions.length;
    const stAll = selectedStates.length === docxStates.length;
    const matAll = selectedMaturities.length === docxMaturities.length;

    if (
      (docxFunctions.length > 0 && selectedFunctions.length === 0) ||
      (docxStates.length > 0 && selectedStates.length === 0) ||
      (docxMaturities.length > 0 && selectedMaturities.length === 0)
    ) {
      return 0;
    }

    const fnSet = new Set(selectedFunctions);
    const stSet = new Set(selectedStates);
    const matSet = new Set(selectedMaturities);
    return docxRequirements.filter(r =>
      (fnAll || fnSet.has(r.function)) &&
      (stAll || stSet.has(r.state)) &&
      (matAll || matSet.has(r.maturity))
    ).length;
  }, [docxRequirements, docxFunctions, docxStates, docxMaturities, selectedFunctions, selectedStates, selectedMaturities]);

  const handleProcessFiles = async () => {
    // Clear any previous success message
    setSuccessMessage('');

    if (!selectedRequirementFile) {
      alert('Please select a requirements file');
      return;
    }


    setTimeout(() => {
      setShowControlPanel(false);
    }, 3000); // Close Control panel after 3 seconds
    setIsProcessing(true);
    setError('');

    try {
      // Legacy .doc uploads are always the narrative ELM/DOORS export —
      // always scoped to the "Functional Requirements" heading regardless
      // of any leftover scope selection from a previous .docx upload.
      const isLegacyDocUpload = /\.doc$/i.test(selectedRequirementFile.name || '');
      const sfdScopeConfig = isLegacyDocUpload
        ? undefined
        : {
            feature_arch: {
              scope_mode: 'artifact_type_list',
              scope_keywords: [
                'Architecture Artifact',
                'Architecture Design',
                'Use Case Design',
                'Non-Functional Requirement',
              ],
            },
            sub_system: {
              scope_mode: 'artifact_type_list',
              scope_keywords: [
                'Subsystem Functional Requirement',
                'Subsystem Diagnostic Requirement',
                'SW Functional Requirement',
                'SW Diagnostic Requirement',
                'Non-Functional Requirement',
              ],
            },
          }[sfdScope];

      const payload = {
        requirements_file_name: selectedRequirementFile.name,
        rules_file_name: selectedRulesFile ? selectedRulesFile.name : null,
        file_standard: selectedRequirementFile.file_standard,
        parser_config: selectedArchitecture
          ? JSON.stringify({ architecture_name: selectedArchitecture })
          : (selectedRequirementFile.file_standard === 'SFD' && sfdScopeConfig)
            ? JSON.stringify(sfdScopeConfig)
            : null,
        enabled_manual_rules: JSON.stringify(selectedManualRules),
        enabled_global_rules: JSON.stringify(selectedGlobalRules),
        selected_functions: selectedRequirementFile.file_standard === 'DOCX' && selectedFunctions.length < docxFunctions.length
          ? JSON.stringify(selectedFunctions)
          : null,
        selected_states: selectedRequirementFile.file_standard === 'DOCX' && selectedStates.length < docxStates.length
          ? JSON.stringify(selectedStates)
          : null,
        selected_maturities: selectedRequirementFile.file_standard === 'DOCX' && selectedMaturities.length < docxMaturities.length
          ? JSON.stringify(selectedMaturities)
          : null,
      };

      const response = await api.submitToQueue(payload);

      if (response.data.job_id) {
        setSuccessMessage(`Job submitted successfully! Job ID: ${response.data.job_id} | Position in queue: ${response.data.queue_position}`);

        setActiveJobs(prev => [...prev, {
          id: response.data.job_id,
          status: 'queued',
          queue_position: response.data.queue_position,
          error: null,
          requirements_file: selectedRequirementFile,
          timestamp: new Date().toISOString()
        }]);

        setSelectedRequirementFile(null);
        setSelectedRulesFile(null);
        setDocxFunctions([]);
        setSelectedFunctions([]);
        setDocxStates([]);
        setSelectedStates([]);
        setDocxMaturities([]);
        setSelectedMaturities([]);
        setDocxRequirements([]);
      }
    } catch (err) {
      console.error('Processing error:', err);
      // Handle different error formats from the backend
      let errorMessage = 'Failed to submit job for processing';
      if (err.response?.data?.detail) {
        // Check if detail is an array of validation errors
        if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
        // Check if detail is a validation error object
        else if (typeof err.response.data.detail === 'object') {
          errorMessage = err.response.data.detail.msg || err.response.data.detail.message || JSON.stringify(err.response.data.detail);
        }
        // If it's a string, use it directly
        else {
          errorMessage = err.response.data.detail;
        }
      }
      setError(errorMessage);
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownloadFile = async (fileName, fileType, folderPath = null, defaultRules = false) => {
    console.log('Download request:', { fileName, fileType, folderPath, defaultRules });
    try {
      const response = await api.downloadFile(userId, fileName, fileType, folderPath, defaultRules);
      console.log('Download response:', response);

      // Use the content-type from the response or default based on file extension
      const contentType = response.headers['content-type'] ||
        (fileName.endsWith('.zip') ? 'application/zip' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download file: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteFile = async (fileName, folderPath, fileType) =>  {

    const confirmed = window.confirm(`Are you sure you want to delete "${fileName}"? This action cannot be undone.`);
      if (!confirmed) return;

    console.log('Delete request:', { fileName, folderPath, fileType });
    try {
      const response = await api.deleteFile(userId, fileName, folderPath, fileType);
      console.log('Delete response:', response);
      if (response.status === 200) {
        setToastMessage(`${fileName} successfully deleted`);
        setTimeout(() => setToastMessage(null), 5000);
      }
      loadFiles();
      setReportsRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete file: '  + (err.response?.data?.detail || err.message));
    }
  };


  const toggleCategoryExpansion = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getCategoryInfo = (categoryKey) => {
    const categoryMap = {
      requirements: { name: 'Requirements', icon: '📋' },
      rules: { name: 'Rules', icon: '📏' },
      reports: { name: 'Reports', icon: '📊' }
    };
    return categoryMap[categoryKey] || { name: categoryKey, icon: '📄' };
  };

  const hasAnyFiles = () => {
    if (!fileList || typeof fileList !== 'object') return false;
    return Object.values(fileList).some(files => Array.isArray(files) && files.length > 0);
  };

  const handleJobSuccess = (jobData) => {
    setActiveJobs(prev =>
      prev.map(job =>
        job.id === jobData.job_id
          ? {
              ...job,
              status: 'success',
              error: null
            }
          : job
      )
    );

    // Highlight the new report in the file list (display name contains first 8 chars of job ID)
    if (jobData?.job_id) {
      setHighlightedReport(jobData.job_id.slice(0, 8));
    }

    // Refresh file list and reports table
    loadFiles();
    setReportsRefreshKey(prev => prev + 1);

  };

  const handleJobError = (jobData) => {
    setActiveJobs(prev =>
      prev.map(job =>
        job.id === jobData.job_id
          ? {
              ...job,
              status: 'failed',
              error: jobData.error
            }
          : job
      )
    );
  };


  const sidebarTabs = [
    { id: 'batch-processing', label: 'Batch Processing', icon: Squares2X2Icon },
    { id: 'single-analysis', label: 'Single Requirement', icon: BeakerIcon },
    { id: 'metrics', label: 'Metrics', icon: ChartBarIcon },
    { id: 'user-observability', label: 'User Observability', icon: UserGroupIcon },
  ];

  const toggleSidebar = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState.toString());
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 mx-auto mb-4 text-primary-500" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <p className="text-neutral-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isUserIdSet) {
    return (
      <UserIdEntry
        userId={userId}
        setUserId={setUserId}
        userEmail={userEmail}
        setUserEmail={setUserEmail}
        handleUserIdSubmit={handleUserIdSubmit}
        error={error}
        authConfig={authConfig}
      />
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      <AppHeader
        showInstructions={showInstructions}
        setShowInstructions={setShowInstructions}
        userId={userId}
        handleLogout={handleLogout}
      />

      <div className="flex">
        {/* Sidebar Navigation */}
        <motion.aside
          initial={{ width: isSidebarCollapsed ? 64 : 256 }}
          animate={{ width: isSidebarCollapsed ? 64 : 256 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="h-[calc(100vh-4rem)] sticky top-16 bg-white dark:bg-neutral-900 border-r border-neutral-200 dark:border-neutral-800 overflow-hidden flex flex-col"
        >

          <nav className="flex-1 p-4 space-y-1">
            {sidebarTabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-300'
                }`}
                title={isSidebarCollapsed ? tab.label : ''}
              >
                <tab.icon className={`${isSidebarCollapsed ? 'w-6 h-6' : 'w-5 h-5'} flex-shrink-0`} />
                <AnimatePresence>
                  {!isSidebarCollapsed && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="font-medium overflow-hidden whitespace-nowrap"
                    >
                      {tab.label}
                    </motion.span>
                  )}
                </AnimatePresence>
                {!isSidebarCollapsed && tab.id === 'jobs' && activeJobs.filter(j => j.status !== 'success' && j.status !== 'failed' && j.status !== 'cancelled').length > 0 && (
                  <span className="ml-auto px-2 py-0.5 text-xs font-semibold bg-primary-500 text-white rounded-full">
                    {activeJobs.filter(j => j.status !== 'success' && j.status !== 'failed' && j.status !== 'cancelled').length}
                  </span>
                )}
              </motion.button>
            ))}
          </nav>

          {/* Collapse/Expand Button at Bottom */}
          <div className="p-4 border-t border-neutral-200 dark:border-neutral-800">
            <button
              onClick={toggleSidebar}
              className={`w-full flex items-center ${isSidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors`}
              title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
              {isSidebarCollapsed ? (
                <ChevronRightIcon className="w-6 h-6 text-neutral-600 dark:text-neutral-400" />
              ) : (
                <>
                  <ChevronLeftIcon className="w-5 h-5 text-neutral-600 dark:text-neutral-400 flex-shrink-0" />
                  <AnimatePresence>
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      transition={{ duration: 0.2 }}
                      className="font-medium text-neutral-700 dark:text-neutral-300 overflow-hidden whitespace-nowrap"
                    >
                      Collapse
                    </motion.span>
                  </AnimatePresence>
                </>
              )}
            </button>
          </div>

        </motion.aside>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-start p-4 w-full overflow-y-auto">
          {/* Hidden JobTracker instances for WebSocket management */}
          {activeJobs
            .filter(job => job.status === 'queued' || job.status === 'processing')
            .map(job => (
              <JobTracker
                key={job.id}
                jobId={job.id}
                userId={userId}
                apiBaseUrl={API_BASE_URL}
                onStatusUpdate={(data) => {
                  if (dismissedJobIdsRef.current.has(job.id)) return;
                  setJobStatuses(prev => ({ ...prev, [job.id]: data }));
                  if (data.status === 'processing') {
                    setActiveJobs(prev =>
                      prev.map(j => j.id === job.id && j.status === 'queued' ? { ...j, status: 'processing' } : j)
                    );
                  }
                }}
                onComplete={(jobData) => {
                  handleJobSuccess(jobData);
                }}
                onError={(jobData) => {
                  handleJobError(jobData);
                }}
                onCancel={(jobData) => {
                  setActiveJobs(prev =>
                    prev.map(j => j.id === jobData.job_id ? { ...j, status: 'cancelled' } : j)
                  );
                }}
              />
            ))}

          {/* Top Progress Bar - shows when any job is processing or just completed */}
          {activeJobs.map(job => {
            const wsStatus = jobStatuses[job.id];
            if (!wsStatus) return null;
            const isActive = wsStatus.status === 'processing' || wsStatus.status === 'success' || wsStatus.status === 'failed' || wsStatus.status === 'cancelled';
            if (!isActive) return null;
            return (
              <TopProgressBar
                key={`progress-${job.id}`}
                job={job}
                status={wsStatus}
                userId={userId}
                apiBaseUrl={API_BASE_URL}
                onCancel={(jobData) => {
                  setActiveJobs(prev =>
                    prev.map(j => j.id === jobData.job_id ? { ...j, status: 'cancelled' } : j)
                  );
                }}
                onDismiss={() => {
                  dismissedJobIdsRef.current.add(job.id);
                  setJobStatuses(prev => {
                    const next = { ...prev };
                    delete next[job.id];
                    return next;
                  });
                }}
              />
            );
          })}

          <AnimatePresence>
            {toastMessage && (
              <motion.div
                key="toast"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg shadow-lg flex items-center justify-between"
              >
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  {toastMessage}
                </span>
                <button
                  onClick={() => setToastMessage(null)}
                  className="text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-200 ml-4 text-lg leading-none"
                >
                  &times;
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {activeTab === 'batch-processing' && (
              <motion.div
                key="batch-processing"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="w-full max-w-[1600px]"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-7xl mx-auto items-start">
                  {/* Left Column: Upload + Jobs */}
                  <div className="flex flex-col gap-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 7rem)' }}>
                    <FileUpload
                        selectedFile={selectedFile}
                        handleFileSelect={handleFileSelect}
                        handleDragOver={handleDragOver}
                        handleDragEnter={handleDragEnter}
                        handleDragLeave={handleDragLeave}
                        handleDrop={handleDrop}
                        handleUpload={handleUpload}
                        isUploading={isUploading}
                        error={error}
                        uploadResult={uploadResult}
                        fileType={fileType}
                        setFileType={setFileType}
                      />

                    {/* Jobs History */}
                    <AnimatePresence>
                      {activeJobs.filter(j => j.status !== 'queued' && j.status !== 'processing').length > 0 && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="card p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                              <BriefcaseIcon className="w-4 h-4" />
                              Recent Jobs
                            </h3>
                            {activeJobs.every(j => j.status === 'success' || j.status === 'failed' || j.status === 'cancelled') && (
                              <button
                                onClick={() => setActiveJobs([])}
                                className="text-xs text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
                              >
                                Dismiss
                              </button>
                            )}
                          </div>
                          <div className="space-y-3">
                            {activeJobs.filter(j => j.status !== 'queued' && j.status !== 'processing').map((job) => (
                              <div
                                key={job.id}
                                className="p-2 rounded-lg bg-neutral-50 dark:bg-neutral-800"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <ClockIcon className="w-4 h-4 text-neutral-500" />
                                    <div>
                                      <p className="text-xs font-medium text-neutral-900 dark:text-white truncate max-w-[180px]">
                                        {job.requirements_file?.name || job.id.slice(0, 8)}
                                      </p>
                                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                                        {job.id.slice(0, 8)}...
                                      </p>
                                    </div>
                                  </div>
                                  <span className={`text-xs px-2 py-1 rounded ${
                                    job.status === 'success' ? 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-400' :
                                    job.status === 'failed' ? 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400' :
                                    'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-400'
                                  }`}>
                                    {job.status === 'success' ? 'Success' :
                                     job.status === 'cancelled' ? 'Cancelled' :
                                     job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                                  </span>
                                </div>
                                {job.status === 'failed' && job.error && (
                                  <div className="mt-2 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white/60 dark:bg-neutral-900/60 p-2 text-xs text-neutral-700 dark:text-neutral-300">
                                    <p>{job.error}</p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* File List and Selection */}
                  <div className="card p-6">
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                          Files & Selection
                        </h2>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={loadFiles}
                          disabled={isLoadingFiles}
                          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-lg transition-all shadow-lg shadow-primary-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:from-neutral-400 disabled:to-neutral-500"
                          title="Refresh file list"
                        >
                          <ArrowPathIcon className={`w-4 h-4 ${isLoadingFiles ? 'animate-spin' : ''}`} />
                          Refresh
                        </motion.button>
                      </div>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        Select requirements and rules files to process
                      </p>
                    </div>

                    {/* Process Files Button */}
                    <div className="mb-4 pb-4 border-b border-neutral-200 dark:border-neutral-700">
                      <button
                        onClick={() => setShowControlPanel(!showControlPanel)}
                        disabled={!selectedRequirementFile}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                          selectedRequirementFile
                            ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
                            : 'bg-neutral-200 dark:bg-neutral-800 text-neutral-400 dark:text-neutral-600 cursor-not-allowed'
                        }`}
                      >
                        <BriefcaseIcon className="w-5 h-5" />
                        Process Selected Files
                      </button>
                      <p className={`mt-2 text-xs text-center text-neutral-500 dark:text-neutral-400 ${
                        selectedRequirementFile ? 'invisible' : ''
                      }`}>
                        Requirements file must be selected
                      </p>
                    </div>

                    <div>
                      <FileList
                        fileList={fileList}
                        isLoadingFiles={isLoadingFiles}
                        hasAnyFiles={hasAnyFiles()}
                        expandedCategories={expandedCategories}
                        toggleCategory={toggleCategoryExpansion}
                        handleSelectFileForProcessing={handleFileSelection}
                        handleDownloadFile={handleDownloadFile}
                        handleDeleteFile={handleDeleteFile}
                        selectedRequirementFile={selectedRequirementFile}
                        selectedRulesFile={selectedRulesFile}
                        getCategoryInfo={getCategoryInfo}
                        highlightedReport={highlightedReport}
                        clearHighlight={() => setHighlightedReport(null)}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-8 max-w-7xl mx-auto">
                  <ReportsTab refreshKey={reportsRefreshKey} />
                </div>
              </motion.div>
            )}

            {activeTab === 'single-analysis' && (
              <motion.div
                key="single-analysis"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="w-full"
              >
                <SingleRequirementAnalysis
                  userId={userId}
                  userEmail={userEmail}
                  selectedRulesFile={selectedRulesFile}
                />
              </motion.div>
            )}

            {activeTab === 'metrics' && (
              <motion.div
                key="metrics"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <MetricsDashboard
                  userId={userId}
                />
              </motion.div>
            )}

            {activeTab === 'user-observability' && (
              <motion.div
                key="user-observability"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="w-full"
              >
                <UserObservability
                  userId={userId}
                />
              </motion.div>
            )}
          </AnimatePresence>

        </main>

        {/* Control Panel Modal */}
        <AnimatePresence>
          {showControlPanel && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                onClick={() => setShowControlPanel(false)}
              />
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 20 }}
                style={{ width: controlPanelWidth }}
                className="fixed right-0 top-0 h-full bg-white dark:bg-neutral-900 shadow-xl z-50 overflow-y-auto"
              >
                {/* Drag handle */}
                <div
                  ref={panelDragRef}
                  className="absolute left-0 top-0 h-full w-1.5 cursor-col-resize z-10 group"
                >
                  <div className="h-full w-full bg-transparent group-hover:bg-primary-400/50 group-active:bg-primary-500/70 transition-colors" />
                </div>
                <ControlPanel
                  selectedRequirementFile={selectedRequirementFile}
                  selectedRulesFile={selectedRulesFile}
                  handleProcessFiles={handleProcessFiles}
                  isProcessing={isProcessing}
                  successMessage={successMessage}
                  onClose={() => setShowControlPanel(false)}
                  manualRulesByDocType={manualRulesByDocType}
                  globalRulesByDocType={globalRulesByDocType}
                  selectedManualRules={selectedManualRules}
                  selectedGlobalRules={selectedGlobalRules}
                  onManualRuleToggle={handleManualRuleToggle}
                  onGlobalRuleToggle={handleGlobalRuleToggle}
                  onToggleAllManualRules={handleToggleAllManualRules}
                  onToggleAllGlobalRules={handleToggleAllGlobalRules}
                  docxFunctions={docxFunctions}
                  selectedFunctions={selectedFunctions}
                  onFunctionToggle={handleFunctionToggle}
                  onToggleAllFunctions={handleToggleAllFunctions}
                  docxStates={docxStates}
                  selectedStates={selectedStates}
                  onStateToggle={handleStateToggle}
                  onToggleAllStates={handleToggleAllStates}
                  docxMaturities={docxMaturities}
                  selectedMaturities={selectedMaturities}
                  onMaturityToggle={handleMaturityToggle}
                  onToggleAllMaturities={handleToggleAllMaturities}
                  isLoadingDocxFilters={isLoadingDocxFilters}
                  filteredRequirementCount={filteredRequirementCount}
                  availableArchitectures={availableArchitectures}
                  selectedArchitecture={selectedArchitecture}
                  onArchitectureChange={setSelectedArchitecture}
                  isLoadingRules={isLoadingRules}
                  sfdScope={sfdScope}
                  onSfdScopeChange={setSfdScope}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
}

export default App;
